import React, { useState, useMemo } from 'react';
import { Box, Dialog, DialogContent, IconButton, Tooltip, Typography, FormControl, Select, MenuItem } from '@mui/material';
import CloseIcon    from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { Line } from 'react-chartjs-2';
import { MONTH_LABELS, getYearSlice } from '../../utils/ragUtils';
import celfinetLogo from '../../../../assets/Cyient_Slate_Std_RGB.png';



export default function Beacon4BlendedChartModal({ open, onClose, series, config, viewYear }) {
    // Modal has its own year selector, initialised from global viewYear
    const [localYear, setLocalYear] = useState(viewYear ?? 'all');

        // Years from actual data only
    const availableYears = useMemo(() => {
        const years = new Set();
        series?.data?.forEach(d => { if (!d.isProjected) years.add(d.year); });
        return [...years].sort();
    }, [series]);

    // Sync when global year changes
    React.useEffect(() => { setLocalYear(viewYear ?? 'all'); }, [viewYear]);

    const data = series?.data ?? [];
    const [sliceFrom, sliceTo] = getYearSlice(data, localYear === 'all' ? null : localYear);

    const chartData = useMemo(() => {
        if (!data.length) return null;
        const sliced = data.slice(sliceFrom, sliceTo + 1);
        const labels       = sliced.map(d => `${MONTH_LABELS[d.month]} ${String(d.year).slice(2)}`);
        const actualValues = sliced.map(d => d.isProjected ? null : d.value);
        const projValues   = sliced.map((d, i) => {
            if (!d.isProjected) return sliced[i + 1]?.isProjected ? d.value : null;
            return d.value;
        });
        const greenLine = sliced.map(d => d.ragGreen ?? null);
        const redLine   = sliced.map(d => d.ragRed   ?? null);
        const lastActualIdx = sliced.reduce((acc, d, i) => (!d.isProjected ? i : acc), -1);
        const markerData    = sliced.map((d, i) => i === lastActualIdx ? d.value : null);

        return {
            labels,
            datasets: [
                { label: 'Actual',          data: actualValues, borderColor: '#378ADD', backgroundColor: 'rgba(55,138,221,0.07)', borderWidth: 2, pointRadius: 0, lineTension: 0.3, fill: true },
                { label: 'Projection',      data: projValues,   borderColor: '#378ADD', backgroundColor: 'rgba(55,138,221,0.03)', borderWidth: 1.5, borderDash: [5,3], pointRadius: 0, lineTension: 0.3, fill: true },
                { label: 'Green threshold', data: greenLine,    borderColor: '#4CAF50', borderWidth: 1, borderDash: [3,4], pointRadius: 0, fill: false, spanGaps: true },
                { label: 'Red threshold',   data: redLine,      borderColor: '#E24B4A', borderWidth: 1, borderDash: [3,4], pointRadius: 0, fill: false, spanGaps: true },
                { label: 'Last actual',     data: markerData,   borderColor: '#E24B4A', backgroundColor: '#E24B4A', borderWidth: 2, pointRadius: markerData.map(v => v != null ? 5 : 0), showLine: false, fill: false },
            ],
        };
    }, [series, sliceFrom, sliceTo]);

    const options = useMemo(() => ({
        responsive: true, maintainAspectRatio: false, animation: { duration: 200 },
        legend: { display: false },
        tooltips: {
            mode: 'index', intersect: false,
            backgroundColor: 'rgba(255,255,255,0.98)', borderColor: 'rgba(0,0,0,0.1)', borderWidth: 1,
            titleFontColor: '#8fa3bc', bodyFontColor: '#1a2233',
            titleFontFamily: 'IBM Plex Mono, monospace', bodyFontFamily: 'IBM Plex Mono, monospace',
            titleFontSize: 10, bodyFontSize: 11, xPadding: 10, yPadding: 10,
            callbacks: {
                label: (item, data) => {
                    const lbl = data.datasets[item.datasetIndex].label || '';
                    if (lbl === 'Last actual') return null;
                    return item.yLabel != null ? ` ${lbl}: ${Number(item.yLabel).toFixed(config?.decimals ?? 2)}` : null;
                },
            },
        },
        scales: {
            xAxes: [{ gridLines: { color: 'rgba(0,0,0,0.04)', lineWidth: 0.5 }, ticks: { fontColor: 'rgba(0,0,0,0.3)', fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, maxTicksLimit: 20, maxRotation: 45 } }],
            yAxes: [{ gridLines: { color: 'rgba(0,0,0,0.04)', lineWidth: 0.5 }, ticks: { fontColor: 'rgba(0,0,0,0.3)', fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, maxTicksLimit: 5, callback: v => Number(v).toFixed(config?.decimals ?? 2) } }],
        },
    }), [config]);

    const handleExport = () => {
        const canvas = document.querySelector('.beacon4-modal-chart canvas');
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `${config?.kpiLabel ?? 'chart'}_${series?.polygon ?? ''}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
            PaperProps={{ sx: { width: '90vw', height: '82vh', borderRadius: 3, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.2, borderBottom: '1px solid #e3e8ef', flexShrink: 0, borderTop: '3px solid #378ADD' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a2233', flex: 1 }}>{config?.kpiLabel}</Typography>
                <Typography sx={{ fontSize: 11, color: '#8fa3bc', mr: 1 }}>{series?.polygon}</Typography>

                {/* Year filter inside modal */}
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select value={localYear} onChange={e => setLocalYear(e.target.value)} sx={{ height: 28, fontSize: 11 }}>
                        <MenuItem value="all" sx={{ fontSize: 12 }}>All years</MenuItem>
                        {availableYears.map(y => <MenuItem key={y} value={y} sx={{ fontSize: 12 }}>{y}</MenuItem>)}
                    </Select>
                </FormControl>

                <Tooltip title="Export as PNG">
                    <IconButton size="small" onClick={handleExport} sx={{ color: '#8fa3bc', '&:hover': { color: '#1565c0' } }}>
                        <DownloadIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={onClose} sx={{ color: '#8fa3bc' }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* Chart */}
            <DialogContent sx={{ p: 2.5, flex: 1, overflow: 'hidden', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                           pointerEvents: 'none', zIndex: 0, width: '30%', display: 'flex', justifyContent: 'center', opacity: 0.05 }}>
                    <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
                </Box>
                <Box className="beacon4-modal-chart" sx={{ height: '100%', position: 'relative', zIndex: 1 }}>
                    {chartData && <Line data={chartData} options={options} />}
                </Box>
            </DialogContent>
        </Dialog>
    );
}